import React, { useState, useEffect, useMemo } from "react";
import { useDataWithRoute, useDebounce } from "../utils/hooks";
import { VsiBubble } from "../styled/App.styled";
import { Col, Row, Spinner } from "reactstrap";
import { snakeCaseToTitleCase } from "../utils/Helpers";
import RadarChart from "../components/RadarChart";
import ColumnChart from "../components/ColumnChart";
import { beforeAfterOld } from "../utils/chartUtils";
import { useVsiAppContext } from "../stores/VsiApp";

const fieldsArr = [
  "text_size_gb",
  "db_size_gb",
  "custom_size_gb",
  "av_size_gb",
  "compress_size_gb",
  "other_size_gb"
];

const toAbbrUpperTitle = field => snakeCaseToTitleCase(field.replace("_gb", "")).split(" ").map(word => word.length === 2 ? word.toUpperCase() : word).join(" ");

export default function FileSavings() {
  const { client: { clientId, name }, getPeriodId } = useVsiAppContext();
  const periodId = getPeriodId("FILES");

  const route = useMemo(() => `/api/v6/report/file_analysis_proc?client_id=${clientId}&period_id=${periodId}`, [periodId, clientId]);
  const [{ recordsets }, { isLoading }] = useDataWithRoute(route);

  const [costPerGbBefore] = useState(parseFloat(localStorage.getItem(`vsi_${name}_file_savings_cost_per_gb_before`) || 2.00));
  const [costPerGbAfter] = useState(parseFloat(localStorage.getItem(`vsi_${name}_file_savings_cost_per_gb_after`) || 1.00));

  const { categories, radarSeries, columnSeries } = useMemo(() => {
    const [grandTotals, oldTotals] = recordsets || [[], []];

    const fullSizes = {};
    const oldSizes = {};

    const dataArr = [];

    const currSeriesData = [];
    const oldSeriesData = [];
    const afterSeriesData = [];


    for (let i = 0; i < fieldsArr.length; i++) {
      const field = fieldsArr[i];
      fullSizes[field] = grandTotals?.[0]?.[field] || 0;
      oldSizes[field] = oldTotals?.[0]?.[field] || 0;

      fullSizes["after_" + field] = fullSizes[field] - oldSizes[field];

      dataArr.push({
        "fileType": toAbbrUpperTitle(field),
        "before": fullSizes[field],
        "after": fullSizes["after_" + field],
        "delta": oldSizes[field],
        "cost_before": fullSizes[field] * costPerGbBefore,
        "cost_after": fullSizes["after_" + field] * costPerGbAfter,
        "field": field
      });

      currSeriesData.push(parseFloat((fullSizes[field] * costPerGbBefore).toFixed(2)));
      oldSeriesData.push(parseFloat((oldSizes[field] * costPerGbBefore).toFixed(2)));
      afterSeriesData.push(parseFloat((fullSizes["after_" + field] * costPerGbAfter).toFixed(2)));
    }

    const radarSeries = [
      {
        name: "Current Cost",
        data: currSeriesData,
        pointPlacement: "on"
      },
      {
        name: "After Moving",
        data: afterSeriesData,
        pointPlacement: "on"
      },
      {
        name: "Old Files Size",
        data: oldSeriesData,
        pointPlacement: "on"
      }
    ];
    const columnSeries = [
      {
        name: "Current Cost",
        data: currSeriesData
      },
      {
        name: "After Moving",
        data: afterSeriesData
      }
    ];

    return {
      categories: fieldsArr.map(toAbbrUpperTitle),
      radarSeries,
      columnSeries,
    }
  }, [recordsets, costPerGbAfter, costPerGbBefore]);

  return isLoading ? (
    <Spinner color={"success"} />
  ) : (
    <VsiBubble>
      <Row>
        <Col lg={5} md={6} xs={12}>
          <VsiBubble>
            <RadarChart series={radarSeries}
                        title={"Savings by Type"}
                        colors={beforeAfterOld}
                        categories={categories}
                        height={380} />
          </VsiBubble>
        </Col>
        <Col lg={7} md={6} xs={12}>
          <VsiBubble>
            <ColumnChart categories={categories}
                         title={"Savings by Type"}
                         colors={beforeAfterOld}
                         series={columnSeries}
                         height={380} />
          </VsiBubble>
        </Col>
      </Row>
    </VsiBubble>
  );
}
